import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import api from '../../config/api';

export default function AccountList() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [error, setError] = useState('');
  const [form, setForm] = useState({
    name: '', type: 'individual', email: '', phone: '',
    address: '', roles: [], notes: '',
    opening_balance: '', opening_balance_date: '', opening_balance_type: 'debit',
    price_level_id: '', payment_terms_days: '', credit_limit: ''
  });

  const { data: accounts = [], isLoading } = useQuery({
    queryKey: ['accounts'],
    queryFn: () => api.get('/api/accounts', { params: { all: true } }).then(r => Array.isArray(r.data) ? r.data : (r.data?.data || [])),
  });
  const { data: settings } = useQuery({
    queryKey: ['settings'],
    queryFn: () => api.get('/api/settings').then(r => r.data),
  });
  const { data: priceLevels = [] } = useQuery({
    queryKey: ['priceLevels'],
    queryFn: () => api.get('/api/price-levels').then(r => r.data),
  });
  const plEnabled = !!settings?.features?.price_levels;

  const saveMutation = useMutation({
    mutationFn: (data) => editing
      ? api.put(`/api/accounts/${editing._id}`, data)
      : api.post('/api/accounts', data),
    onSuccess: () => {
      queryClient.invalidateQueries(['accounts']);
      setShowForm(false);
      setEditing(null);
      resetForm();
    },
    onError: (err) => setError(err.response?.data?.error || t('error')),
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => api.delete(`/api/accounts/${id}`),
    onSuccess: () => queryClient.invalidateQueries(['accounts']),
    onError: (err) => alert(err.response?.data?.error || t('error')),
  });

  const resetForm = () => setForm({
    name: '', type: 'individual', email: '', phone: '',
    address: '', roles: [], notes: '',
    opening_balance: '', opening_balance_date: '', opening_balance_type: 'debit',
    price_level_id: '', payment_terms_days: '', credit_limit: ''
  });

  const handleEdit = (acc) => {
    setEditing(acc);
    setForm({
      name: acc.name, type: acc.type, email: acc.email || '',
      phone: acc.phone || '', address: acc.address || '',
      roles: acc.roles || [], notes: acc.notes || '',
      opening_balance: acc.opening_balance || '',
      opening_balance_date: acc.opening_balance_date
        ? new Date(acc.opening_balance_date).toISOString().split('T')[0] : '',
      opening_balance_type: acc.opening_balance_type || 'debit',
      price_level_id: acc.price_level_id || '',
      payment_terms_days: acc.payment_terms_days || '',
      credit_limit: acc.credit_limit || ''
    });
    setShowForm(true);
  };

  const toggleRole = (role) => {
    setForm(f => ({
      ...f,
      roles: f.roles.includes(role)
        ? f.roles.filter(r => r !== role)
        : [...f.roles, role]
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setError('');
    saveMutation.mutate(form);
  };

  const inputCls = "w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500";

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-gray-800">{t('accounts')}</h2>
        <button
          onClick={() => { setShowForm(true); setEditing(null); resetForm(); }}
          className="bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium px-4 py-2 rounded-lg"
        >
          + {t('add')}
        </button>
      </div>

      {showForm && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">
            {editing ? t('edit') : t('add')} {t('accounts')}
          </h3>
          <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Basic Info */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('name')} *</label>
              <input type="text" value={form.name}
                onChange={e => setForm({ ...form, name: e.target.value })} required className={inputCls} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('type')} *</label>
              <select value={form.type} onChange={e => setForm({ ...form, type: e.target.value })} className={inputCls}>
                <option value="individual">Individual</option>
                <option value="business">Business</option>
                <option value="cash">Cash</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Roles</label>
              <div className="flex gap-4 mt-2">
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={form.roles.includes('supplier')} onChange={() => toggleRole('supplier')} />
                  Supplier
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={form.roles.includes('customer')} onChange={() => toggleRole('customer')} />
                  Customer
                </label>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('email')}</label>
              <input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} className={inputCls} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
              <input type="text" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} className={inputCls} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
              <input type="text" value={form.address} onChange={e => setForm({ ...form, address: e.target.value })} className={inputCls} />
            </div>

            {/* Price Level — only when feature is ON */}
            {plEnabled && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Price Level</label>
                <select
                  value={form.price_level_id}
                  onChange={e => setForm({ ...form, price_level_id: e.target.value })}
                  className={inputCls}
                >
                  <option value="">— Base price —</option>
                  {priceLevels.map(lvl => (
                    <option key={lvl._id} value={lvl._id}>{lvl.name_en}</option>
                  ))}
                </select>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Payment terms (days)</label>
              <input type="number" min="0" value={form.payment_terms_days} placeholder="0 = pay on the spot"
                onChange={e => setForm({ ...form, payment_terms_days: e.target.value })} className={inputCls} />
              <p className="text-xs text-gray-400 mt-1">Invoices on credit fall due this many days after their date.</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Credit limit (USD)</label>
              <input type="number" min="0" step="0.01" value={form.credit_limit} placeholder="0 = no limit"
                onChange={e => setForm({ ...form, credit_limit: e.target.value })} className={inputCls} />
              <p className="text-xs text-gray-400 mt-1">A sale that takes the customer over it is refused (owner/manager can allow it).</p>
            </div>

            <div className="md:col-span-3">
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('notes')}</label>
              <textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} rows={2} className={inputCls} />
            </div>

            {/* Opening Balance Section */}
            <div className="md:col-span-3 border-t border-gray-100 pt-4 mt-1">
              <p className="text-sm font-semibold text-gray-700 mb-3">Opening Balance <span className="text-gray-400 font-normal">(optional — balance before using this system)</span></p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Amount</label>
                  <input type="number" value={form.opening_balance} min="0" step="0.01"
                    placeholder="0.00"
                    onChange={e => setForm({ ...form, opening_balance: e.target.value })}
                    className={inputCls} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">As of Date</label>
                  <input type="date" value={form.opening_balance_date}
                    onChange={e => setForm({ ...form, opening_balance_date: e.target.value })}
                    className={inputCls} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                  <select value={form.opening_balance_type}
                    onChange={e => setForm({ ...form, opening_balance_type: e.target.value })}
                    className={inputCls}>
                    <option value="debit">Debit — They owe you</option>
                    <option value="credit">Credit — You owe them</option>
                  </select>
                </div>
              </div>
            </div>

            {error && <div className="md:col-span-3 text-red-600 text-sm">{error}</div>}
            <div className="md:col-span-3 flex gap-3">
              <button type="submit" disabled={saveMutation.isPending}
                className="bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium px-6 py-2 rounded-lg disabled:opacity-50">
                {saveMutation.isPending ? t('loading') : t('save')}
              </button>
              <button type="button" onClick={() => { setShowForm(false); setEditing(null); }}
                className="bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-medium px-6 py-2 rounded-lg">
                {t('cancel')}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* mobile cards */}
      <div className="md:hidden space-y-2">
        {isLoading ? (
          <div className="bg-white rounded-xl border border-gray-200 p-8 text-center text-gray-500">{t('loading')}</div>
        ) : accounts.length === 0 ? (
          <AccountEmptyState onAdd={() => { setShowForm(true); setEditing(null); resetForm(); }} />
        ) : accounts.map(acc => (
          <div key={acc._id} className="bg-white rounded-xl border border-gray-200 shadow-sm p-3">
            <div className="flex items-center justify-between mb-1">
              <button onClick={() => navigate(`/accounts/${acc._id}/ledger`)}
                className="font-semibold text-gray-800 text-sm text-left">{acc.name}</button>
              <span className={`px-2 py-0.5 rounded-full text-[10.5px] font-medium ${acc.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                {acc.status === 'active' ? t('active') : t('inactive')}
              </span>
            </div>
            <div className="flex items-center gap-1.5 flex-wrap text-xs text-gray-500 mb-2">
              <span className="capitalize">{acc.type}</span>
              {acc.roles?.map(r => (
                <span key={r} className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full text-[10.5px] font-medium capitalize">{r}</span>
              ))}
              {acc.phone && <span>· {acc.phone}</span>}
              {acc.opening_balance ? (
                <span className={acc.opening_balance_type === 'credit' ? 'text-red-500' : 'text-orange-500'}>· OB ${acc.opening_balance}</span>
              ) : null}
            </div>
            <div className="flex gap-4 text-xs font-medium border-t border-gray-100 pt-2">
              <button onClick={() => navigate(`/accounts/${acc._id}/ledger`)} className="text-purple-600">Ledger</button>
              <button onClick={() => navigate(`/accounts/${acc._id}/statement`)} className="text-indigo-600">Statement</button>
              <button onClick={() => handleEdit(acc)} className="text-blue-600">{t('edit')}</button>
              {!acc.is_default && (
                <button onClick={() => { if (window.confirm('Delete this account?')) deleteMutation.mutate(acc._id); }}
                  className="text-red-500 ml-auto">{t('delete')}</button>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* desktop table */}
      <div className="hidden md:block bg-white rounded-xl shadow-sm border border-gray-200">
        {isLoading ? (
          <div className="p-8 text-center text-gray-500">{t('loading')}</div>
        ) : accounts.length === 0 ? (
          <AccountEmptyState onAdd={() => { setShowForm(true); setEditing(null); resetForm(); }} />
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-left px-6 py-3 font-semibold text-gray-600">{t('name')}</th>
                <th className="text-left px-6 py-3 font-semibold text-gray-600">{t('type')}</th>
                <th className="text-left px-6 py-3 font-semibold text-gray-600">Roles</th>
                <th className="text-left px-6 py-3 font-semibold text-gray-600">Phone</th>
                <th className="text-left px-6 py-3 font-semibold text-gray-600">Opening Bal.</th>
                <th className="text-left px-6 py-3 font-semibold text-gray-600">{t('status')}</th>
                <th className="text-left px-6 py-3 font-semibold text-gray-600">{t('actions')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {accounts.map(acc => (
                <tr key={acc._id} className="hover:bg-gray-50">
                  <td className="px-6 py-3 font-medium text-gray-800">
                    <button onClick={() => navigate(`/accounts/${acc._id}/ledger`)}
                      className="hover:text-blue-600 hover:underline transition-colors">
                      {acc.name}
                    </button>
                  </td>
                  <td className="px-6 py-3 text-gray-600 capitalize">{acc.type}</td>
                  <td className="px-6 py-3">
                    <div className="flex gap-1">
                      {acc.roles?.map(r => (
                        <span key={r} className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full text-xs font-medium capitalize">{r}</span>
                      ))}
                    </div>
                  </td>
                  <td className="px-6 py-3 text-gray-600">{acc.phone || '—'}</td>
                  <td className="px-6 py-3 text-gray-600">
                    {acc.opening_balance
                      ? <span className={acc.opening_balance_type === 'credit' ? 'text-red-500' : 'text-orange-500'}>
                          ${acc.opening_balance}
                        </span>
                      : <span className="text-gray-300">—</span>}
                  </td>
                  <td className="px-6 py-3">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${acc.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                      {acc.status === 'active' ? t('active') : t('inactive')}
                    </span>
                  </td>
                  <td className="px-6 py-3">
                    <div className="flex gap-2">
                      <button onClick={() => navigate(`/accounts/${acc._id}/ledger`)}
                        className="text-purple-600 hover:text-purple-800 font-medium">Ledger</button>
                      <button onClick={() => navigate(`/accounts/${acc._id}/statement`)}
                        className="text-indigo-600 hover:text-indigo-800 font-medium">Statement</button>
                      <button onClick={() => handleEdit(acc)} className="text-blue-600 hover:text-blue-800 font-medium">{t('edit')}</button>
                      {!acc.is_default && (
                        <button onClick={() => { if (window.confirm('Delete this account?')) deleteMutation.mutate(acc._id); }}
                          className="text-red-500 hover:text-red-700 font-medium">{t('delete')}</button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function AccountEmptyState({ onAdd }) {
  return (
    <div className="py-16 px-6 text-center">
      <div className="text-5xl mb-3">👥</div>
      <div className="font-bold text-gray-700 text-base mb-2">ما في حسابات بعد / No accounts yet</div>
      <div className="text-gray-400 text-sm mb-5">
        أضف عملاءك وموردينك لتتبع الديون والمدفوعات<br/>
        Add your customers &amp; suppliers to track balances
      </div>
      <button
        onClick={onAdd}
        className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-white font-bold text-sm"
        style={{ background: '#2e9f86' }}
      >
        + أضف حساب / Add Account
      </button>
    </div>
  );
}